import _ from "lodash";
import React, { Fragment, useRef } from "react";
import classes from "./ActionTable.scss";
import { CheckboxOn } from "SvgComponents";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import { generateRandomId, getStyleStrippedText } from "Utils";
import update from "immutability-helper";
import { useI18n } from "Hooks";
import classNames from "classnames";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  AddOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import { colors } from "Constants";

const DEFAULT_ROWS_TO_INIT = 1;

export const ActionTable = props => {
  const {
    columns = [],
    rows = [],
    onRowAdd = () => {},
    onCellValueChange = () => {},
    onRowSelect = () => {},
    onRowDelete = () => {},
    updateFocusCount,
    isTableEmpty,
    disabled,
    displayDisabledAsActive,
    showCheckbox,
    showPrompts,
    promptTooltipWidth,
    customStyle,
  } = props;

  /*******************************************************************************************************
   * displayDisabledAsActive flag is used to control the visual state of ActionTable component,
   * where if it is true, we will show the element in visually active form but
   * functionality wise it will behave like disabled.
   ********************************************************************************************************/

  const { t } = useI18n(["common", "a11y"]);
  const cellsRef = useRef({});
  const contentEditableStyle = {
    maxHeight: "200px",
    overflowY: "auto",
    cursor: disabled ? "not-allowed" : "text",
  };

  const getDisabledCheckBoxStyles = ({ isChecked, disabled }) => {
    return disabled && !displayDisabledAsActive
      ? {
          backgroundColor: isChecked
            ? colors.textDisabled
            : colors.surfaceDisabled,
          borderColor: isChecked
            ? colors.textDefault
            : colors.surfaceDefaultHover,
        }
      : {};
  };

  return (
    <Fragment>
      <table className={classes.table} style={customStyle}>
        <thead>
          <tr>
            <th
              className={classes.deleteIconCell}
              aria-label={t("a11y:delete_table_row")}
            />
            {showCheckbox ? (
              <th
                className={classes.checkboxCell}
                aria-label={t("a11y:select_row_for_bulk_action")}
              ></th>
            ) : null}
            {_.map(columns, column => {
              const { id, label, promptLabel } = column;
              return (
                <th className={classes.tableCell} key={id}>
                  {label}
                  {/* prompt with tooltip should be shown only in the edit mode */}
                  {showPrompts && !disabled ? (
                    <Tooltip
                      tooltip={promptLabel}
                      placement={"left"}
                      overlayInnerStyle={{
                        width: promptTooltipWidth,
                      }}
                    >
                      <InformationOutlined />
                    </Tooltip>
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {_.map(rows, row => {
            const { id: rowId, data = [], isChecked = false } = row;
            return (
              <tr key={rowId} className={classes.row}>
                <td className={classes.deleteIconCell}>
                  <div className={classes.deleteIcon}>
                    {!isTableEmpty && !disabled && (
                      <IconButton
                        variant="plain"
                        onClick={() => {
                          onRowDelete({ rowId });
                        }}
                        icon={<DeleteOutlined />}
                      />
                    )}
                  </div>
                </td>
                {showCheckbox ? (
                  <td
                    className={classes.checkboxCell}
                    aria-label={t("a11y:select_action_table_row")}
                  >
                    <div className={classes.checkboxCellContent}>
                      <div
                        className={classNames({
                          [classes.selectedradioButton]: isChecked,
                          [classes.radiobutton]: !isChecked,
                          [classes.cursorNotAllowed]: disabled,
                        })}
                        style={getDisabledCheckBoxStyles({
                          isChecked,
                          disabled,
                        })}
                        onClick={() => {
                          !disabled &&
                            onRowSelect({
                              rowId,
                              isChecked: !isChecked,
                            });
                        }}
                      >
                        {isChecked ? <CheckboxOn /> : null}
                      </div>
                    </div>
                  </td>
                ) : null}
                {_.map(data, cell => {
                  const { id: cellId } = cell;
                  let { value: cellValue } = cell;
                  cellValue = getStyleStrippedText({ text: cellValue }).replace(
                    /<br \/>/g,
                    "<br>"
                  );

                  return (
                    <td
                      key={cellId}
                      cellPadding={0}
                      cellSpacing={0}
                      onClick={() => cellsRef.current[cellId].focus()}
                      className={classNames({
                        [classes.cursorNotAllowed]: disabled,
                        [classes.tableCell]: true,
                      })}
                    >
                      <UIBaseContentEditable
                        disabled={disabled}
                        ref={ref => (cellsRef.current[cellId] = ref)}
                        name={"label"}
                        mode={"edit"}
                        allowEnter={true}
                        style={contentEditableStyle}
                        shouldPasteAsPlainText={false}
                        shouldParseInPlainText={false}
                        updateInputField={params => {
                          onCellValueChange({
                            rowId,
                            cellId,
                            value: params.label,
                          });
                        }}
                        value={cellValue}
                        onFocusInputField={() => updateFocusCount({ value: 1 })}
                        onBlurInputField={() => updateFocusCount({ value: -1 })}
                        styleStripType={"CONTENT_EDITABLE"}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!(disabled && displayDisabledAsActive) && (
        <div className={classes.actionButton}>
          <Button
            onClick={onRowAdd}
            variant={"outlined-subtle"}
            disabled={disabled}
            icon={<AddOutlined />}
          >
            {t("common:add_a_with_label", {
              label: t("common:row"),
            })}
          </Button>
        </div>
      )}
    </Fragment>
  );
};

const ActionTableFieldComponent = props => {
  const {
    fieldTypeConfig: {
      columns: templateColumns = {},
      columnsList: templateColoumnList = [],
      customStyle,
      showPrompts,
      promptTooltipWidth,
    } = {},
    fieldKey,
    updateInputField,
    onBlurInputField,
    onFocusInputField,
    value: { rows: valueRows = [], columns: valueColumns = [] } = {},
    disabled = false,
    displayDisabledAsActive = false,
    showCheckbox = true,
  } = props;
  const focusedCount = useRef(0);
  const templateColArr = _.map(templateColoumnList, columnId => {
    return templateColumns[columnId];
  });

  const columns = valueColumns.length ? valueColumns : templateColArr;

  const updateField = params => {
    updateInputField({
      [fieldKey]: {
        rows,
        columns,
        ...params,
      },
    });
  };

  const getNewRow = () => {
    return {
      id: generateRandomId(),
      isChecked: false,
      data: _.map(columns, col => {
        return {
          id: generateRandomId(),
          uid: col.id,
          value: "",
        };
      }),
    };
  };

  const rows = (() => {
    if (valueRows.length) {
      return valueRows;
    }

    /**
     * @description : by default create new rows
     */
    return _.map([...Array(DEFAULT_ROWS_TO_INIT)], () => {
      return getNewRow();
    });
  })();

  const onRowAdd = () => {
    const row = getNewRow();
    updateField({
      rows: [...rows, row],
    });
  };

  const onRowDelete = ({ rowId }) => {
    const updatedRows = _.filter(rows, row => {
      return row.id != rowId;
    });
    updateField({
      rows: updatedRows,
    });
  };

  const onCellValueChange = ({ rowId, cellId, value }) => {
    const targetRowIndex = _.findIndex(rows, { id: rowId });
    const targetCellIndex = _.findIndex(rows[targetRowIndex].data, {
      id: cellId,
    });
    const updatedRows = update(rows, {
      [targetRowIndex]: {
        data: {
          [targetCellIndex]: {
            value: {
              $set: value,
            },
          },
        },
      },
    });

    updateField({
      rows: updatedRows,
    });
  };

  const onRowSelect = value => {
    const { rowId } = value;
    const targetRowIndex = _.findIndex(rows, { id: rowId });
    const updatedRows = update(rows, {
      [targetRowIndex]: {
        isChecked: {
          $set: value.isChecked,
        },
      },
    });
    updateField({
      rows: updatedRows,
    });
  };
  const updateFocusCount = ({ value }) => {
    const updatedFocusedCount = focusedCount.current + value;

    if (focusedCount.current == 0 && updatedFocusedCount != 0) {
      onFocusInputField({ key: "actionPlan", id: null });
    } else if (updatedFocusedCount == 0 && focusedCount.current != 0) {
      onBlurInputField({ key: "actionPlan", id: null });
    }
    focusedCount.current = updatedFocusedCount;
  };

  //If all the rows are empty or if row is created but no data has been added to any column.
  const isTableEmpty =
    _.isEmpty(valueRows) ||
    (_.isEqual(_.size(rows), 1) &&
      _.isEmpty(_.filter(_.first(rows)?.data, item => item?.value)));

  return (
    <ActionTable
      columns={columns}
      rows={rows}
      onRowAdd={onRowAdd}
      onRowDelete={onRowDelete}
      onCellValueChange={onCellValueChange}
      onRowSelect={onRowSelect}
      updateFocusCount={updateFocusCount}
      isTableEmpty={isTableEmpty}
      disabled={disabled}
      displayDisabledAsActive={displayDisabledAsActive}
      showCheckbox={showCheckbox}
      showPrompts={showPrompts}
      promptTooltipWidth={promptTooltipWidth}
      customStyle={customStyle}
    />
  );
};

class ActionTableUIComponent extends UIBaseComponent {
  constructor(props) {
    super(props);
  }

  isValidValue = value => {
    const { rows, columns } = value || {};
    return !(_.isEmpty(rows) || _.isEmpty(columns));
  };

  renderView = () => {
    const { props } = this;

    return (
      <ActionTableFieldComponent
        {...props}
        disabled={true}
        displayDisabledAsActive={true}
      />
    );
  };

  renderEdit = () => {
    const { props } = this;
    return <ActionTableFieldComponent {...props} />;
  };
}

ActionTableUIComponent.defaultProps = {
  ...UIBaseComponent.defaultProps,
  editContainerStyle: { flexDirection: "column" },
};

export default ActionTableUIComponent;
